import { createBrowserRouter, type RouteObject } from 'react-router-dom'
import { ProtectedRoutes } from 'contexts/ProtectedRoutes'
import { ErrorBoundary } from 'components/ErrorBoundary'
import {
  loginRouterPath,
  TREELESS_SESSION_ROUTE,
  SESSION_ROUTE,
  SESSION_ROUTE_EXTERNAL,
  SESSION_ROUTE_EXTERNAL_LEGACY,
} from 'routes'
export {
  loginRouterPath,
  TREELESS_SESSION_ROUTE,
  SESSION_ROUTE,
  SESSION_ROUTE_EXTERNAL,
  SESSION_ROUTE_EXTERNAL_LEGACY,
}

export const routerConfig: RouteObject[] = [
  {
    path: loginRouterPath,
    lazy: async () => {
      const { Component } = (await import('pages/login/Login')).default
      return {
        Component: () => (
          <ErrorBoundary>
            <Component />
          </ErrorBoundary>
        ),
      }
    },
  },
  {
    // Entry point for cross-domain OAuth: branded domains redirect here so that
    // signInWithRedirect is initiated on the same origin as the Cognito callback URL.
    path: 'launch-auth',
    lazy: async () => {
      const { Component } = (await import('pages/session/LaunchAuth')).default
      return {
        Component: () => (
          <ErrorBoundary>
            <Component />
          </ErrorBoundary>
        ),
      }
    },
  },
  {
    path: '',
    element: (
      <ErrorBoundary>
        <ProtectedRoutes />
      </ErrorBoundary>
    ),
    children: [
      {
        index: true,
        path: '',
        lazy: async () => (await import('pages/home/HomeRoute')).default,
      },
      {
        path: 'treeless/symptoms',
        lazy: async () =>
          (await import('pages/treeless/SearchSymptomsRoute')).default,
      },
      {
        path: TREELESS_SESSION_ROUTE,
        lazy: async () =>
          (await import('pages/session/TreelessSession')).default,
      },
      {
        path: 'resume',
        lazy: async () => (await import('pages/resume')).default,
      },
      {
        path: SESSION_ROUTE,
        lazy: async () => (await import('pages/session/SessionRoute')).default,
      },
      {
        path: 'tempUrl/:projectId/:tempToken',
        lazy: async () =>
          (await import('pages/session/SessionTempToken')).default,
      },
      {
        path: 'allowSession/:projectId',
        lazy: async () => (await import('pages/session/AllowSession')).default,
      },
      {
        path: 'launchExternalSession/:projectId',
        lazy: async () =>
          (await import('pages/session/LaunchExternalSession')).default,
      },
      {
        path: 'auth/callback',

        lazy: async () =>
          (await import('pages/session/SessionAuthCallback')).default,
      },
    ],
  },
  {
    path: SESSION_ROUTE_EXTERNAL_LEGACY,
    lazy: async () => {
      const { Component } = (await import('pages/session/SessionRoute')).default
      return {
        Component: () => (
          <ErrorBoundary>
            <Component />
          </ErrorBoundary>
        ),
      }
    },
  },
  {
    path: SESSION_ROUTE_EXTERNAL,
    lazy: async () => {
      const { Component } = (await import('pages/session/SessionRoute')).default
      return {
        Component: () => (
          <ErrorBoundary>
            <Component />
          </ErrorBoundary>
        ),
      }
    },
  },
]

const getBasePath = (isLibraryMode: boolean) => {
  if (isLibraryMode) {
    // Extract the base path from current URL for library mode
    const pathSegments = window.location.pathname.split('/')
    const appNameIndex = pathSegments.findIndex(segment => segment === 'tsp')

    if (appNameIndex >= 2) {
      // Return path up to and including appName: /:company/:portalName/appName
      return pathSegments.slice(0, appNameIndex + 1).join('/')
    }

    // Fallback if pattern doesn't match
    throw Error('cannot setup base path')
  }

  return '/' // Default for standalone mode
}

export const createAppRouter = (isLibraryMode = false) =>
  createBrowserRouter(routerConfig, {
    basename: getBasePath(isLibraryMode),
    future: {
      v7_relativeSplatPath: true,
    },
  })
